import { useRef, useState } from 'react';
import { HiReceiptRefund } from 'react-icons/hi';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { mutate } from 'swr';

import { ModalContent } from 'components';
import { integerToCurrency } from 'helpers';
import { returnRequestService } from 'services';

interface ReturnRequestModalProps {
  protocol: string;
  value: number;
  onClose: () => void;
}

export function ReturnRequestModal({
  protocol,
  value,
  onClose,
}: ReturnRequestModalProps) {
  const { searchKey } = useParams<{ searchKey: string }>();
  const passwordRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const password = passwordRef.current?.value?.trim();

    if (!password) return;

    setIsLoading(true);

    try {
      const { errors } = await returnRequestService.postRefund({
        protocol,
        password,
      });

      if (!errors) {
        toast.success('Devolução realizada com sucesso!');
        mutate(`consignment-and-return-${searchKey}`);
      }

      onClose();
    } catch (error) {
      console.error('Erro ao processar devolução:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalContent handleClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col w-[320px] p-2">
        <div className="pb-2">
          <p className="text-base font-bold text-neutral-700">
            Realizando devolução
          </p>
          <p className="text-xs text-neutral-700">Protocolo {protocol}</p>
        </div>
        <div className="py-4">
          <div className="flex items-center gap-1">
            <HiReceiptRefund className="text-success-300" />
            <p className="text-sm font-bold text-neutral-500">
              Estorno da devolução
            </p>
          </div>
          <p className="text-sm text-neutral-500">{integerToCurrency(value)}</p>
        </div>
        <div className="pt-4">
          <p className="text-xs text-neutral-500 pb-1">
            Digite sua senha para confirmar{' '}
            <span className="text-danger-300">*</span>
          </p>
          <input
            ref={passwordRef}
            id="password"
            type="password"
            required
            className="h-10 px-4 py-2 rounded-lg bg-neutral-100 border border-neutral-300 text-xs
              border-solid text-neutral-500 w-full"
            placeholder="************"
          />
        </div>
        <div className="flex gap-4 pt-6">
          <button
            type="button"
            onClick={onClose}
            className="flex justify-center items-center flex-grow h-10 px-4 py-2 rounded-lg
              bg-neutral-500 text-sm text-neutral-100"
          >
            Voltar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex justify-center items-center flex-grow h-10 px-4 py-2 rounded-lg
              bg-primary-300 text-sm text-neutral-100 disabled:opacity-50
              disabled:cursor-not-allowed"
          >
            {isLoading ? 'Processando...' : 'Realizar devolução'}
          </button>
        </div>
      </form>
    </ModalContent>
  );
}
